import React, { useState, useEffect, useRef } from 'react';
import { FiChevronDown, FiCheck } from 'react-icons/fi';

/**
 * BIFilterSelect
 * Componente unificado para dropdowns e selects com o visual popover idêntico ao BI Metas.
 * Substitui os <select> nativos garantindo consistência, ícones e estados ativos.
 */
export default function BIFilterSelect({
  label,
  value,
  onChange,
  options = [],
  allLabel = 'Todos',
  allValue = '',
  minWidth = '140px',
  icon: TriggerIcon = null,
  title = '',
  style = {}
}) {
  const containerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  // Fechar ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Fechar com ESC
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const selectedOpt = options.find(
    (opt) => String(opt.value) === String(value)
  );

  const isFiltered = Boolean(value && String(value) !== String(allValue));
  const displayLabel = selectedOpt ? selectedOpt.label : allLabel;
  const SelectedIcon = selectedOpt?.icon || TriggerIcon;

  const handleSelect = (val) => {
    setIsOpen(false);
    if (onChange) {
      onChange(val);
    }
  };

  return (
    <div className="bi-filter-dropdown-container" ref={containerRef} style={{ minWidth, ...style }}>
      {label && <span className="bi-filter-label">{label}</span>}
      <div
        className={`bi-filter-trigger-box ${isOpen ? 'open' : ''} ${isFiltered ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title={title || label}
      >
        <div className="bi-filter-trigger-content">
          {SelectedIcon && (
            <SelectedIcon size={13} className="text-primary opacity-75" />
          )}
          <span style={{ fontSize: '0.82rem' }}>{displayLabel}</span>
        </div>
        <FiChevronDown size={14} className={`bi-filter-chevron ${isOpen ? 'open' : ''}`} />
      </div>

      {isOpen && (
        <div className="bi-filter-popover-panel" style={{ zIndex: 1070 }}>
          <div className="bi-popover-header">
            <span className="bi-popover-meta-title">{label || 'Opções'}</span>
            <span className="bi-popover-count-tag">{options.length} opções</span>
          </div>

          <div className="bi-popover-list" style={{ maxHeight: '250px' }}>
            {/* Opção padrão "Todos" */}
            <div
              className={`bi-popover-item ${!isFiltered ? 'selected' : ''}`}
              onClick={() => handleSelect(allValue)}
            >
              <div className="bi-popover-item-left">
                {TriggerIcon && <TriggerIcon size={13} className="text-muted" />}
                <span className="fw-semibold text-dark" style={{ fontSize: '0.82rem' }}>
                  {allLabel}
                </span>
              </div>
              {!isFiltered && (
                <FiCheck size={14} className="text-primary fw-bold" />
              )}
            </div>

            {/* Lista de opções */}
            {options.map((opt) => {
              const isSelected = String(opt.value) === String(value);
              const OptIcon = opt.icon;
              return (
                <div
                  key={String(opt.value)}
                  className={`bi-popover-item ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleSelect(opt.value)}
                >
                  <div className="bi-popover-item-left">
                    {OptIcon && <OptIcon size={13} className={isSelected ? 'text-primary' : 'text-muted'} />}
                    <span className="fw-semibold text-dark" style={{ fontSize: '0.82rem' }}>
                      {opt.label}
                    </span>
                  </div>
                  {isSelected && (
                    <FiCheck size={14} className="text-primary fw-bold" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
